// Two-panel page for supervisors to link packing materials (boxes, trays, bags) to HTE finished products
// Left panel: "Default (All Products)" entry + searchable list of HTE products. Right panel: linked packing materials with packing level.

import React, { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Search, Plus, Trash2, X, Link2, Package, ChevronRight, BoxSelect } from 'lucide-react';
import packingMaterialMappingService, {
  PackingMaterialMapping as PackingMapping,
  PackingMaterialSearchResult,
} from '../../services/packingMaterialMappingService';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';

const WILDCARD = '*';

interface SelectedPackingItem extends PackingMaterialSearchResult {
  packing_level: 'case' | 'subunit';
}

const PackingMaterialMappingPage: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedPackingItem[]>([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Queries
  const { data: products = [], isLoading: loadingProducts } = useQuery(
    ['packing-products'],
    packingMaterialMappingService.getProducts
  );

  const { data: mappings = [], isLoading: loadingMappings } = useQuery(
    ['packing-mappings', selectedProduct],
    () => selectedProduct ? packingMaterialMappingService.getMappings(selectedProduct) : Promise.resolve([]),
    { enabled: !!selectedProduct }
  );

  // Fetch all mappings for the overview panel
  const { data: allMappings = [], isLoading: loadingAllMappings } = useQuery(
    ['packing-mappings-all'],
    () => packingMaterialMappingService.getMappings()
  );

  const { data: searchResults = [], isFetching: searching } = useQuery(
    ['packing-search', itemSearch],
    () => packingMaterialMappingService.searchPackingMaterials(itemSearch),
    { enabled: itemSearch.length >= 2, keepPreviousData: true }
  );

  // Split mappings for the selected product into defaults vs product-specific
  const { defaultMappings, productMappings } = useMemo(() => {
    if (selectedProduct === WILDCARD) {
      return { defaultMappings: [], productMappings: mappings.filter(m => m.product_item_number === WILDCARD) };
    }
    return {
      defaultMappings: mappings.filter(m => m.product_item_number === WILDCARD),
      productMappings: mappings.filter(m => m.product_item_number !== WILDCARD),
    };
  }, [mappings, selectedProduct]);

  // Mutations

  const updateLevelMutation = useMutation(
    ({ id, packing_level }: { id: number; packing_level: 'case' | 'subunit' }) =>
      packingMaterialMappingService.updateMapping(id, { packing_level }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['packing-mappings', selectedProduct]);
        queryClient.invalidateQueries(['packing-mappings-all']);
      },
      onError: () => {
        toast.error('Failed to update packing level');
      },
    }
  );

  const deleteMutation = useMutation(packingMaterialMappingService.deleteMapping, {
    onSuccess: () => {
      toast.success(t('packingMaterialMapping.mappingDeleted'));
      queryClient.invalidateQueries(['packing-mappings', selectedProduct]);
      queryClient.invalidateQueries(['packing-mappings-all']);
      setConfirmDeleteId(null);
    },
    onError: () => {
      toast.error('Failed to delete mapping');
    },
  });

  // Filter products by search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const q = productSearch.toLowerCase();
    return products.filter(
      (p) =>
        p.item_number.toLowerCase().includes(q) ||
        (p.item_english_name || '').toLowerCase().includes(q) ||
        (p.item_chinese_name || '').toLowerCase().includes(q)
    );
  }, [products, productSearch]);

  // Filter search results to exclude already-linked and already-selected items
  const filteredSearchResults = useMemo(() => {
    const linkedNumbers = new Set(mappings
      .filter(m => m.product_item_number === selectedProduct)
      .map((m) => m.packing_item_number));
    const selectedNumbers = new Set(selectedItems.map((i) => i.item_number));
    return searchResults.filter((item) => !linkedNumbers.has(item.item_number) && !selectedNumbers.has(item.item_number));
  }, [searchResults, mappings, selectedProduct, selectedItems]);

  // Group all mappings by product for the overview panel
  const groupedMappings = useMemo(() => {
    const groups: Record<string, { productName: string | null; productChineseName: string | null; items: PackingMapping[] }> = {};
    for (const m of allMappings) {
      if (!groups[m.product_item_number]) {
        groups[m.product_item_number] = {
          productName: m.product_item_number === WILDCARD ? t('packingMaterialMapping.defaultAllProducts') : m.product_name,
          productChineseName: m.product_item_number === WILDCARD ? null : m.product_chinese_name,
          items: [],
        };
      }
      groups[m.product_item_number].items.push(m);
    }
    // Sort: wildcard first, then alphabetically by product name
    return Object.entries(groups).sort((a, b) => {
      if (a[0] === WILDCARD) return -1;
      if (b[0] === WILDCARD) return 1;
      const nameA = (a[1].productName || a[0]).toLowerCase();
      const nameB = (b[1].productName || b[0]).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [allMappings, t]);

  const handleCreate = async () => {
    if (!selectedProduct || selectedItems.length === 0) return;
    setIsSaving(true);
    let successCount = 0;
    for (const item of selectedItems) {
      try {
        await packingMaterialMappingService.createMapping({
          product_item_number: selectedProduct,
          packing_item_number: item.item_number,
          packing_level: item.packing_level,
          notes: newNotes || undefined,
          created_by: user?.id || user?.username || undefined,
        });
        successCount++;
      } catch (error: any) {
        const msg = error?.response?.data?.error || `Failed to add ${item.item_english_name || item.item_number}`;
        toast.error(msg);
      }
    }
    if (successCount > 0) {
      toast.success(`${successCount} ${t('packingMaterialMapping.mappingCreated')}`);
      queryClient.invalidateQueries(['packing-mappings', selectedProduct]);
      queryClient.invalidateQueries(['packing-mappings-all']);
    }
    setIsSaving(false);
    setShowAddModal(false);
    setItemSearch('');
    setNewNotes('');
    setSelectedItems([]);
  };

  const selectedProductInfo = selectedProduct === WILDCARD
    ? null
    : products.find((p) => p.item_number === selectedProduct);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('packingMaterialMapping.subtitle')}
        </p>
      </div>

      {/* Two-panel layout */}
      <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
        {/* Left panel: Products */}
        <div className="w-full md:w-1/3 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('packingMaterialMapping.selectProduct')}
            </h3>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder={t('packingMaterialMapping.searchProducts')}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {/* Default (All Products) entry - always at top */}
            <button
              onClick={() => setSelectedProduct(WILDCARD)}
              className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                selectedProduct === WILDCARD
                  ? 'bg-primary-50 dark:bg-primary-900/30 border-l-2 border-l-primary-500'
                  : ''
              }`}
            >
              <div className="flex items-center gap-2">
                <BoxSelect className="h-4 w-4 text-primary-500 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {t('packingMaterialMapping.defaultAllProducts')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">*</div>
                </div>
              </div>
            </button>

            {loadingProducts ? (
              <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
            ) : filteredProducts.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                {t('packingMaterialMapping.noResults')}
              </div>
            ) : (
              filteredProducts.map((p) => (
                <button
                  key={p.item_number}
                  onClick={() => setSelectedProduct(p.item_number)}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    selectedProduct === p.item_number
                      ? 'bg-primary-50 dark:bg-primary-900/30 border-l-2 border-l-primary-500'
                      : ''
                  }`}
                >
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {p.item_english_name || p.item_number}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {p.item_number}
                    {p.item_chinese_name && ` · ${p.item_chinese_name}`}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Right panel: Selected product editing + existing linkages overview */}
        <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="flex-1 overflow-y-auto">
            {/* Selected product editing section */}
            {selectedProduct && (
              <div>
                {/* Header with close button */}
                <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      onClick={() => setSelectedProduct(null)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {t('packingMaterialMapping.linkedPackingMaterials')}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                        {selectedProduct === WILDCARD
                          ? t('packingMaterialMapping.defaultAllProducts')
                          : (selectedProductInfo?.item_english_name || selectedProduct) +
                            (selectedProductInfo?.item_chinese_name ? ` (${selectedProductInfo.item_chinese_name})` : '')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowAddModal(true);
                      setItemSearch('');
                      setSelectedItems([]);
                      setNewNotes('');
                    }}
                    className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors flex-shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('packingMaterialMapping.addPackingMaterial')}</span>
                  </button>
                </div>

                {/* Default mappings section (shown when viewing a specific product, not wildcard) */}
                {selectedProduct !== WILDCARD && defaultMappings.length > 0 && (
                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('packingMaterialMapping.defaultAllProducts')}
                      </span>
                    </div>
                    {defaultMappings.map((mapping) => (
                      <div
                        key={mapping.id}
                        className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-700/20"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {mapping.packing_name || mapping.packing_item_number}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {mapping.packing_item_number}
                            {mapping.packing_chinese_name && ` · ${mapping.packing_chinese_name}`}
                          </div>
                          {mapping.notes && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                              {mapping.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                          <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                            mapping.packing_level === 'subunit'
                              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300'
                              : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                          }`}>
                            {t(`packingMaterialMapping.level_${mapping.packing_level || 'case'}`)}
                          </span>
                          {Number(mapping.packing_unit_weight) > 0 && (
                            <span className="px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded">
                              {Number(mapping.packing_unit_weight).toFixed(2)} lbs
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Product-specific mappings list */}
                <div>
                  {selectedProduct !== WILDCARD && productMappings.length === 0 && defaultMappings.length > 0 && (
                    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('packingMaterialMapping.title')}
                      </span>
                    </div>
                  )}
                  {loadingMappings ? (
                    <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
                  ) : productMappings.length === 0 ? (
                    <div className="flex items-center justify-center p-6 text-gray-400 dark:text-gray-500">
                      <div className="text-center">
                        <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{t('packingMaterialMapping.noPackingMaterialsLinked')}</p>
                      </div>
                    </div>
                  ) : (
                    productMappings.map((mapping) => (
                      <div
                        key={mapping.id}
                        className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {mapping.packing_name || mapping.packing_item_number}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {mapping.packing_item_number}
                            {mapping.packing_chinese_name && ` · ${mapping.packing_chinese_name}`}
                          </div>
                          {mapping.notes && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">
                              {mapping.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          <div className="flex items-center gap-0">
                            <button
                              onClick={() => mapping.packing_level !== 'case' && updateLevelMutation.mutate({ id: mapping.id, packing_level: 'case' })}
                              className={`px-2 py-0.5 text-xs font-medium rounded-l transition-colors ${
                                (mapping.packing_level || 'case') === 'case'
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                              }`}
                            >
                              {t('packingMaterialMapping.level_case')}
                            </button>
                            <button
                              onClick={() => mapping.packing_level !== 'subunit' && updateLevelMutation.mutate({ id: mapping.id, packing_level: 'subunit' })}
                              className={`px-2 py-0.5 text-xs font-medium rounded-r transition-colors ${
                                mapping.packing_level === 'subunit'
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                              }`}
                            >
                              {t('packingMaterialMapping.level_subunit')}
                            </button>
                          </div>
                          {Number(mapping.packing_unit_weight) > 0 && (
                            <div className="px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded">
                              {Number(mapping.packing_unit_weight).toFixed(2)} lbs
                            </div>
                          )}
                          {confirmDeleteId === mapping.id ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-red-600 dark:text-red-400">
                                {t('packingMaterialMapping.confirmRemove')}
                              </span>
                              <button
                                onClick={() => deleteMutation.mutate(mapping.id)}
                                disabled={deleteMutation.isLoading}
                                className="px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                              >
                                {t('common.yes')}
                              </button>
                              <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded transition-colors"
                              >
                                {t('common.no')}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmDeleteId(mapping.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 dark:hover:text-red-400 rounded transition-colors"
                              title={t('packingMaterialMapping.removePackingMaterial')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Existing Linkages overview - always visible */}
            <div>
              <div className={`p-3 border-b border-gray-200 dark:border-gray-700 ${selectedProduct ? 'border-t-2 border-t-gray-300 dark:border-t-gray-600' : ''}`}>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('packingMaterialMapping.existingLinkages')}
                </h3>
              </div>
              {loadingAllMappings ? (
                <div className="p-4 text-center text-sm text-gray-500">Loading...</div>
              ) : groupedMappings.length === 0 ? (
                <div className="flex items-center justify-center p-8 text-gray-400 dark:text-gray-500">
                  <div className="text-center">
                    <Link2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{t('packingMaterialMapping.noMappingsYet')}</p>
                  </div>
                </div>
              ) : (
                groupedMappings.map(([productNumber, group]) => (
                  <div
                    key={productNumber}
                    className={`border-b border-gray-200 dark:border-gray-700 ${selectedProduct === productNumber ? 'bg-primary-50/50 dark:bg-primary-900/20' : ''}`}
                  >
                    <button
                      onClick={() => setSelectedProduct(productNumber)}
                      className="w-full text-left px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors group"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {group.productName || productNumber}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {productNumber}
                          {group.productChineseName && ` · ${group.productChineseName}`}
                          <span className="ml-2 text-primary-500">
                            {t('packingMaterialMapping.packingMaterialCount').replace('{{count}}', String(group.items.length))}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary-500 flex-shrink-0 ml-2" />
                    </button>
                    <div className="pl-6 pr-4 pb-2">
                      {group.items.map((item) => (
                        <div key={item.id} className="py-1.5 flex items-center gap-2">
                          <span className="text-gray-300 dark:text-gray-600 text-xs">•</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-gray-700 dark:text-gray-300 truncate">
                              {item.packing_name || item.packing_item_number}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
                              {item.packing_item_number}
                              {item.packing_chinese_name && ` · ${item.packing_chinese_name}`}
                              {Number(item.packing_unit_weight) > 0 && (
                                <span className="ml-1">· {Number(item.packing_unit_weight).toFixed(2)} lbs</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-0 flex-shrink-0">
                            <button
                              onClick={(e) => { e.stopPropagation(); item.packing_level !== 'case' && updateLevelMutation.mutate({ id: item.id, packing_level: 'case' }); }}
                              className={`px-2 py-0.5 text-xs font-medium rounded-l transition-colors ${
                                (item.packing_level || 'case') === 'case'
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                              }`}
                            >
                              {t('packingMaterialMapping.level_case')}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); item.packing_level !== 'subunit' && updateLevelMutation.mutate({ id: item.id, packing_level: 'subunit' }); }}
                              className={`px-2 py-0.5 text-xs font-medium rounded-r transition-colors ${
                                item.packing_level === 'subunit'
                                  ? 'bg-orange-500 text-white'
                                  : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                              }`}
                            >
                              {t('packingMaterialMapping.level_subunit')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add Packing Material Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('packingMaterialMapping.addPackingMaterial')}
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-4 space-y-4">
              {/* Search input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('packingMaterialMapping.searchItems')}
                </label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder={t('packingMaterialMapping.searchItems')}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    ref={searchInputRef}
                    autoFocus
                  />
                </div>
              </div>

              {/* Search results */}
              {itemSearch.length >= 2 && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md">
                  {searching ? (
                    <div className="p-3 text-center text-sm text-gray-500">Loading...</div>
                  ) : filteredSearchResults.length === 0 ? (
                    <div className="p-3 text-center text-sm text-gray-500">
                      {searchResults.length > 0 && filteredSearchResults.length === 0
                        ? t('packingMaterialMapping.alreadyLinked')
                        : t('packingMaterialMapping.noResults')}
                    </div>
                  ) : (
                    filteredSearchResults.map((item) => (
                      <button
                        key={item.item_number}
                        onClick={() => {
                          setSelectedItems((prev) => [...prev, { ...item, packing_level: 'case' }]);
                          setItemSearch('');
                          setTimeout(() => searchInputRef.current?.focus(), 0);
                        }}
                        className="w-full text-left px-3 py-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {item.item_english_name || item.item_number}
                          </div>
                          {(Number(item.unit_weight) > 0 || item.item_unit) && (
                            <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                              {Number(item.unit_weight) > 0 && (
                                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                                  {Number(item.unit_weight).toFixed(2)} lbs
                                </span>
                              )}
                              {item.item_unit && (
                                <span className="text-xs font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
                                  {item.item_unit}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {item.item_number}
                          {item.item_chinese_name && ` · ${item.item_chinese_name}`}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* Selected items display */}
              {selectedItems.length > 0 && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                    {selectedItems.length} selected
                  </label>
                  {selectedItems.map((item) => (
                    <div key={item.item_number} className="p-2 bg-primary-50 dark:bg-primary-900/20 rounded-md border border-primary-200 dark:border-primary-800">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium text-primary-700 dark:text-primary-300">
                          {item.item_english_name || item.item_number}
                        </div>
                        <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                          {Number(item.unit_weight) > 0 && (
                            <span className="text-xs font-medium text-primary-600 dark:text-primary-300 bg-primary-100 dark:bg-primary-800/40 px-1.5 py-0.5 rounded">
                              {Number(item.unit_weight).toFixed(2)} lbs
                            </span>
                          )}
                          {item.item_unit && (
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">
                              {item.item_unit}
                            </span>
                          )}
                          <button
                            onClick={() => setSelectedItems((prev) => prev.filter((i) => i.item_number !== item.item_number))}
                            className="p-0.5 text-red-400 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 rounded transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-xs text-primary-600 dark:text-primary-400">
                          {item.item_number}
                          {item.item_chinese_name && ` · ${item.item_chinese_name}`}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => setSelectedItems((prev) => prev.map((i) => i.item_number === item.item_number ? { ...i, packing_level: 'case' } : i))}
                            className={`px-2 py-0.5 text-xs font-medium rounded-l transition-colors ${
                              item.packing_level === 'case'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                            }`}
                          >
                            {t('packingMaterialMapping.level_case')}
                          </button>
                          <button
                            onClick={() => setSelectedItems((prev) => prev.map((i) => i.item_number === item.item_number ? { ...i, packing_level: 'subunit' } : i))}
                            className={`px-2 py-0.5 text-xs font-medium rounded-r transition-colors ${
                              item.packing_level === 'subunit'
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                            }`}
                          >
                            {t('packingMaterialMapping.level_subunit')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('packingMaterialMapping.notes')}
                </label>
                <input
                  type="text"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder={`${t('packingMaterialMapping.notes')} (${t('common.optional')})`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Modal footer */}
            <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded-md transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreate}
                disabled={selectedItems.length === 0 || isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
              >
                {isSaving ? 'Saving...' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingMaterialMappingPage;
